import { Scene, Tilemaps } from "phaser";
import { EventBus } from "../../EventBus.ts";
import { Player } from "../../classes/player.ts";
import { Monster } from "../../classes/monster.ts";
import { Mrpas } from "mrpas";
import { EVENTS_NAME, MAP_DIM_VAL } from "../../consts.ts";
import { gameObjectsToObjectPoints } from "../../helpers/gameobject-to-object-point.ts";
import Vector2 = Phaser.Math.Vector2;
import { Text } from "../../classes/text.ts";
import * as EasyStar from "easystarjs";

const SIGHT_RADIUS = 15; // in tiles
const OFF_TINT = 0x404040;
const ON_TINT = 0xffffff;
export const FOV = false;
const ALLOW_ZOOM = false;

interface NamedObject {
  name: string;
}

export class BaseScene extends Scene {
  player1!: Player;
  map!: Tilemaps.Tilemap;
  groundLayer!: Tilemaps.TilemapLayer;
  wallsLayer!: Tilemaps.TilemapLayer;
  overlayLayer!: Tilemaps.TilemapLayer;
  overlayLayer2!: Tilemaps.TilemapLayer;
  vehiclesLayer!: Tilemaps.TilemapLayer;
  npcLayer!: Tilemaps.TilemapLayer;
  fov?: Mrpas;
  mapName = "Tutorial level";
  target: Vector2;
  storylineSprites: Phaser.Types.Physics.Arcade.SpriteWithDynamicBody[] = [];
  challengeSprites: Phaser.Types.Physics.Arcade.SpriteWithDynamicBody[] = [];
  zoneSprites: Phaser.Types.Physics.Arcade.SpriteWithDynamicBody[] = [];
  storylineOn: boolean = false;
  challengeOn: boolean = false;
  zoneOn: boolean = false;
  zoneId: string = "";
  easystar!: EasyStar.js;

  // Nameplates floated over manually-placed NPCs (the NPCs tile layer).
  npcNameplates: Text[] = [];

  // NB the movement tween chain lives on the Player (`player.moveTweenChain`),
  // NOT here: moveCharacter drives player1 AND every remote player, so a
  // single shared slot meant one character's movement cancelled another's.
  //
  // Track the current EasyStar path calculation instance
  currentPathInstanceId: number | null = null;

  moveCharacter(player: Player, path: any[]): void {
    // Stop any existing tween chain to prevent conflicting movements
    if (player.moveTweenChain) {
      player.moveTweenChain.stop();
      player.moveTweenChain = null;
    }
    const tweenConfigs: Phaser.Types.Tweens.TweenBuilderConfig[] = [];
    for (let i = 0; i < path.length - 1; i++) {
      var ex = path[i + 1].x;
      var ey = path[i + 1].y;
      var sx = path[i].x;
      var sy = path[i].y;

      // Calculate direction vector
      var dx = ex - sx;
      var dy = ey - sy;

      // Calculate velocity based on direction and player's VELOCITY constant
      var velocityX =
        dx !== 0 ? (dx > 0 ? player.VELOCITY : -player.VELOCITY) : 0;
      var velocityY =
        dy !== 0 ? (dy > 0 ? player.VELOCITY : -player.VELOCITY) : 0;

      // If moving diagonally, adjust velocity to maintain consistent speed
      if (velocityX !== 0 && velocityY !== 0) {
        const velFactor = Math.sqrt(2) / 2;
        velocityX *= velFactor;
        velocityY *= velFactor;
      }

      // Determine the direction based on the dominant axis of movement
      let direction: string;
      if (Math.abs(dx) > Math.abs(dy)) {
        // Horizontal movement is dominant
        direction = dx > 0 ? "right" : "left";
      } else {
        // Vertical movement is dominant
        direction = dy > 0 ? "down" : "up";
      }

      // Move at the player's WASD speed (VELOCITY px/s) so click-to-move is
      // as fast as keyboard movement, instead of a fixed per-tile duration.
      const stepDistance = Math.sqrt(dx * dx + dy * dy) * MAP_DIM_VAL;
      const stepDuration = (stepDistance / player.VELOCITY) * 1000;

      tweenConfigs.push({
        targets: player,
        x: { value: ex * MAP_DIM_VAL, duration: stepDuration },
        y: { value: ey * MAP_DIM_VAL, duration: stepDuration },
        onStart: () => {
          // console.log(
          //   `start movement for ${player.account_name} in ${direction} direction`,
          // );
          player.direction = direction;
          player.anims.play(player.avatarAnim(`run-${direction}`), true);
        },
        onUpdate: () => {
          player.anims.play(player.avatarAnim(`run-${direction}`), true);
        },
      });
    }
    // Store reference to the new tween chain
    player.moveTweenChain = this.tweens.chain({
      tweens: tweenConfigs,
      onComplete: () => {
        player.body!.reset(
          path[path.length - 1].x * MAP_DIM_VAL,
          path[path.length - 1].y * MAP_DIM_VAL,
        );
        player.setVelocity(0);
        player.moveTweenChain = null; // Clear reference when complete
        if (player.account_name === this.player1.account_name) {
          EventBus.emit(EVENTS_NAME.playerMove, {
            zone: 1,
            x: player.x,
            y: player.y,
          });
        }
      },
    });
  }

  initPath(): void {
    this.easystar = new EasyStar.js();
    if (!this.wallsLayer || !this.map) {
      return;
    }
    const grid: number[][] = [];
    const mapWidth = this.map.width;
    const mapHeight = this.map.height;
    // Initialize the grid with 0s (walkable)
    for (let y = 0; y < mapHeight; y++) {
      grid[y] = [];
      for (let x = 0; x < mapWidth; x++) {
        // Get the tile at this position
        const tile = this.wallsLayer.getTileAt(x, y);
        // If there's no tile or the tile index is 0, it's walkable (0)
        // Otherwise, it's a wall (1)
        grid[y][x] = !tile ? 0 : 1;
      }
    }
    this.easystar.setGrid(grid);
    this.easystar.setAcceptableTiles([0]); // Only tile index 0 is walkable
  }

  initCamera(): void {
    this.cameras.main.setSize(this.game.scale.width, this.game.scale.height);
    this.cameras.main.startFollow(this.player1, true, 0.09, 0.09);
    this.cameras.main.setZoom(1);

    if (ALLOW_ZOOM) {
      this.input.on(
        "wheel",
        (
          _pointer: Phaser.Input.Pointer,
          _gameObjects: Phaser.GameObjects.GameObject[],
          _deltaX: number,
          deltaY: number,
          _deltaZ: number,
        ) => {
          const newZoom =
            this.cameras.main.zoom - this.cameras.main.zoom * 0.001 * deltaY;
          this.cameras.main.zoom = Phaser.Math.Clamp(newZoom, 0.25, 2);
        },
      );
    }

    this.target = new Vector2();
    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      // Get the WORLD x and y position of the pointer
      const { worldX, worldY } = pointer;

      // Assign the world x and y to our vector
      this.target.x = worldX;
      this.target.y = worldY;

      // Stop any existing movement immediately
      if (this.player1.moveTweenChain) {
        this.player1.moveTweenChain.stop();
        this.player1.moveTweenChain = null;
      }
      this.player1.setVelocity(0);

      //this.physics.moveToObject(this.player1, this.target, 110);
      const toX = Math.floor(worldX / MAP_DIM_VAL);
      const toY = Math.floor(worldY / MAP_DIM_VAL);

      // Always use the player's current position as the starting point
      // This ensures smooth transitions when a new path is requested while an existing one is in progress
      const fromX = Math.floor(this.player1.x / MAP_DIM_VAL);
      const fromY = Math.floor(this.player1.y / MAP_DIM_VAL);

      // Cancel previous pathfinding if any
      if (this.currentPathInstanceId !== null) {
        this.easystar.cancelPath(this.currentPathInstanceId);
      }

      // console.log(
      //   "going from (" + fromX + "," + fromY + ") to (" + toX + "," + toY + ")",
      // );
      // this.player1.shake.shake();
      try {
        this.currentPathInstanceId = this.easystar.findPath(
          fromX,
          fromY,
          toX,
          toY,
          (path) => {
            this.currentPathInstanceId = null;
            if (path === null) {
              console.warn("Path was not found.");
            } else {
              // console.log(path);
              this.moveCharacter(this.player1, path);
            }
          },
        );
        this.easystar.calculate();
      } catch (error) {
        console.error("Error calculating path (ignored):", error);
      }
    });
  }

  computeFOV(sight_radius: number = SIGHT_RADIUS) {
    if (!this.fov || !this.map || !this.wallsLayer || !this.player1) {
      return;
    }

    // get camera view bounds
    const camera = this.cameras.main;
    const lx = this.map.worldToTileX(camera.worldView.x) || 0;
    const ly = this.map.worldToTileY(camera.worldView.y) || 0;
    const lw = this.map.worldToTileX(camera.worldView.width) || 0;
    const lh = this.map.worldToTileY(camera.worldView.height) || 0;
    const bounds = new Phaser.Geom.Rectangle(lx - 1, ly - 1, lw + 2, lh + 3);

    // set all tiles within camera view to invisible
    for (let y = bounds.y; y < bounds.y + bounds.height; y++) {
      for (let x = bounds.x; x < bounds.x + bounds.width; x++) {
        if (y < 0 || y >= this.map.height || x < 0 || x >= this.map.width) {
          continue;
        }
        const gtile = this.groundLayer.getTileAt(x, y);
        if (gtile) {
          gtile.alpha = 1;
          gtile.tint = OFF_TINT;
        }
        const tile = this.overlayLayer.getTileAt(x, y);
        if (tile) {
          tile.alpha = 1;
          tile.tint = OFF_TINT;
        }
        const otile = this.overlayLayer2.getTileAt(x, y);
        if (otile) {
          otile.alpha = 1;
          otile.tint = OFF_TINT;
        }
        const wtile = this.wallsLayer.getTileAt(x, y);
        if (wtile) {
          wtile.alpha = 1;
          wtile.tint = OFF_TINT;
        }
        if (this.vehiclesLayer) {
          const ftile = this.vehiclesLayer.getTileAt(x, y);
          if (ftile) {
            ftile.alpha = 1;
            ftile.tint = OFF_TINT;
          }
        }
        if (this.vehiclesLayer) {
          const dtile = this.vehiclesLayer.getTileAt(x, y);
          if (dtile) {
            dtile.alpha = 1;
            dtile.tint = OFF_TINT;
          }
        }
        // Manually-placed NPC tiles dim with the rest of the world (out of
        // sight = darkened, not hidden) so they don't float bright in the fog.
        if (this.npcLayer) {
          const ntile = this.npcLayer.getTileAt(x, y);
          if (ntile) {
            ntile.alpha = 1;
            ntile.tint = OFF_TINT;
          }
        }
      }
    }

    // compute fov from player's position
    const px = this.map.worldToTileX(this.player1.x) || 0;
    const py = this.map.worldToTileY(this.player1.y) || 0;
    // compute fov from player's position
    this.fov.compute(
      px,
      py,
      sight_radius,
      (x, y) => {
        const gtile = this.groundLayer!.getTileAt(x, y);
        const tile = this.overlayLayer!.getTileAt(x, y);
        const wtile = this.wallsLayer!.getTileAt(x, y);
        let ftile: Tilemaps.Tile | null = null;
        if (this.vehiclesLayer) {
          ftile = this.vehiclesLayer.getTileAt(x, y);
        }
        let bgtile = false;
        let btile = false;
        let bwtile = false;
        let bftile = false;
        if (gtile) {
          bgtile = gtile.tint === ON_TINT;
        }
        if (tile) {
          btile = tile.tint === ON_TINT;
        }
        if (bwtile) {
          bwtile = wtile.tint === ON_TINT;
        }
        if (ftile) {
          bftile = ftile.tint === ON_TINT;
        }
        return btile || bwtile || bftile || bgtile;
      },
      (x, y) => {
        const d = Phaser.Math.Distance.Between(py, px, y, x);
        const alpha = Math.min(2 - d / sight_radius - 0.5, 1);
        const tile = this.overlayLayer!.getTileAt(x, y);
        const otile = this.overlayLayer2!.getTileAt(x, y);
        const wtile = this.wallsLayer!.getTileAt(x, y);
        if (tile) {
          tile.alpha = alpha;
          tile.tint = ON_TINT;
        }
        if (otile) {
          otile.alpha = alpha;
          otile.tint = ON_TINT;
        }
        if (wtile) {
          wtile.alpha = alpha;
          wtile.tint = ON_TINT;
        }
        const ntile = this.npcLayer ? this.npcLayer.getTileAt(x, y) : null;
        if (ntile) {
          ntile.alpha = alpha;
          ntile.tint = ON_TINT;
        }
      },
    );
  }

  initStorylines(): void {
    const storylines = gameObjectsToObjectPoints(
      this.map.filterObjects("Storylines", (obj) => obj.name === "Storyline"),
    );
    const storylineList = storylines.map((storyline) => {
      const sprite = this.physics.add
        .sprite(
          storyline.x + storyline.width / 2,
          storyline.y + storyline.height / 2,
          "tilesMob",
        )
        .setSize(storyline.width, storyline.height)
        .setAlpha(0);
      // Read properties BY NAME (order-independent): story_title, story, story_id.
      (storyline.properties || []).forEach((p: { name: string; value: unknown }) =>
        sprite.setData(p.name, p.value),
      );
      this.storylineSprites.push(sprite);
      return sprite;
    });
    storylineList.forEach((storyline) => {
      this.physics.add.overlap(this.player1, storyline, (_obj1, obj2) => {
        if (!(obj2 instanceof Tilemaps.Tile) && !this.storylineOn) {
          this.storylineOn = true;
          EventBus.emit(EVENTS_NAME.showStoryline, obj2);
        }
      });
    });
  }

  // Word lists for the random NPC handle generator. Quirky + on-theme so a
  // name reveals nothing about whether the NPC is human or AI (the Turing-test
  // hook) — and so "Sky Buns"-style handles can still emerge.
  private static readonly NPC_NAME_A = [
    "Glitch", "Neon", "Static", "Cipher", "Echo", "Vapor", "Hex", "Null",
    "Chrome", "Pixel", "Ghost", "Drift", "Volt", "Quartz", "Onyx", "Rune",
    "Sky", "Iron", "Fuzz", "Mercury", "Cobalt", "Ember",
  ];
  private static readonly NPC_NAME_B = [
    "Fox", "Buns", "Runner", "Wraith", "Moth", "Fawn", "Crow", "Kid",
    "Wolf", "Saint", "Diver", "Knot", "Spark", "Husk", "Lark", "Vandal",
    "Otter", "Hound", "Finch", "Maven",
  ];

  private leet(s: string): string {
    const m: Record<string, string> = {
      o: "0", e: "3", i: "1", a: "4", s: "5",
    };
    return s
      .split("")
      .map((c) => (Math.random() < 0.4 ? m[c.toLowerCase()] ?? c : c))
      .join("");
  }

  private randomNpcName(used: Set<string>): string {
    for (let attempt = 0; attempt < 30; attempt++) {
      const a =
        BaseScene.NPC_NAME_A[
          Math.floor(Math.random() * BaseScene.NPC_NAME_A.length)
        ];
      const b =
        BaseScene.NPC_NAME_B[
          Math.floor(Math.random() * BaseScene.NPC_NAME_B.length)
        ];
      const r = Math.random();
      let name: string;
      if (r < 0.34) name = `${a} ${b}`; // "Sky Buns"
      else if (r < 0.67) name = this.leet(`${a}${b}`); // "Gl1tchf0x"
      else name = `${this.leet(a)}_${b.toLowerCase()}`; // "n3on_runner"
      if (!used.has(name)) {
        used.add(name);
        return name;
      }
    }
    const fallback = `unit_${used.size}`;
    used.add(fallback);
    return fallback;
  }

  /**
   * Float a randomly-generated nameplate over each NPC painted on the NPCs
   * tile layer. NPCs are located by clustering connected non-empty tiles
   * (8-connectivity); each plate is centered horizontally over its cluster and
   * anchored just above the top edge — like last year's badges sitting over the
   * NPC's head, but positions come from the map instead of being hardcoded.
   */
  protected initNpcNameplates(): void {
    if (!this.npcLayer || !this.map) {
      return;
    }
    const W = this.map.width;
    const H = this.map.height;
    const seen = new Set<number>();
    const used = new Set<string>();
    const key = (x: number, y: number) => y * W + x;
    const present = (x: number, y: number) =>
      x >= 0 && y >= 0 && x < W && y < H && !!this.npcLayer.getTileAt(x, y);

    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        if (!present(x, y) || seen.has(key(x, y))) {
          continue;
        }
        // Flood-fill this NPC's connected tiles (8-way) to get its extent.
        let minX = x, maxX = x, minY = y, maxY = y;
        const stack: [number, number][] = [[x, y]];
        seen.add(key(x, y));
        while (stack.length) {
          const [cx, cy] = stack.pop()!;
          if (cx < minX) minX = cx;
          if (cx > maxX) maxX = cx;
          if (cy < minY) minY = cy;
          if (cy > maxY) maxY = cy;
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              const nx = cx + dx;
              const ny = cy + dy;
              if (present(nx, ny) && !seen.has(key(nx, ny))) {
                seen.add(key(nx, ny));
                stack.push([nx, ny]);
              }
            }
          }
        }
        const left = this.map.tileToWorldX(minX) ?? minX * 32;
        const right = (this.map.tileToWorldX(maxX) ?? maxX * 32) + 32;
        const top = this.map.tileToWorldY(minY) ?? minY * 32;
        const plate = new Text(
          this,
          (left + right) / 2,
          top + 11, // nudged down ~15px from the cluster's top edge
          this.randomNpcName(used),
        ).setFontSize(14);
        plate.setOrigin(0.5, 1); // center horizontally, sit above the head
        plate.setDepth(1000);
        this.npcNameplates.push(plate);
      }
    }
  }

  initChallenges(): void {
    const challenges = gameObjectsToObjectPoints(
      this.map.filterObjects("Challenges", (obj) => obj.name === "Challenge"),
    );
    const challengeList = challenges.map((challenge) => {
      const sprite = this.physics.add
        .sprite(
          challenge.x + challenge.width / 2,
          challenge.y + challenge.height / 2,
          "tilesMob",
        )
        .setSize(challenge.width, challenge.height)
        .setAlpha(0);
      // Read properties BY NAME (order-independent). Static Challenges are
      // client-only, so they DO keep challenge_text/challenge_title/name here.
      (challenge.properties || []).forEach((p: { name: string; value: unknown }) =>
        sprite.setData(p.name, p.value),
      );
      this.challengeSprites.push(sprite);
      return sprite;
    });
    challengeList.forEach((challenge) => {
      this.physics.add.overlap(this.player1, challenge, (_obj1, obj2) => {
        if (!(obj2 instanceof Tilemaps.Tile) && !this.challengeOn) {
          this.challengeOn = true;
          EventBus.emit(EVENTS_NAME.showChallenge, obj2);
        }
      });
    });
  }

  initInteractiveChallenges(): void {
    const ichallenges = gameObjectsToObjectPoints(
      this.map.filterObjects(
        "Challenges",
        (obj) => obj.name === "InteractiveChallenge",
      ),
    );
    const ichallengeList = ichallenges.map((ichallenge) => {
      const sprite = this.physics.add
        .sprite(
          ichallenge.x + ichallenge.width / 2,
          ichallenge.y + ichallenge.height / 2,
          "tilesMob",
        )
        .setSize(ichallenge.width, ichallenge.height)
        .setAlpha(0);
      // Read every property BY NAME (order-independent). InteractiveChallenge
      // objects now carry only the identifiers needed to fetch from the server
      // (challenge_id, mission_giver_id, objective_id) — title/text/type/payload
      // all arrive in the server's InteractNPCResponse.
      (ichallenge.properties || []).forEach((p: { name: string; value: unknown }) =>
        sprite.setData(p.name, p.value),
      );
      this.challengeSprites.push(sprite);
      return sprite;
    });
    ichallengeList.forEach((ichallenge) => {
      this.physics.add.overlap(this.player1, ichallenge, (_obj1, obj2) => {
        if (!(obj2 instanceof Tilemaps.Tile) && !this.challengeOn) {
          this.challengeOn = true;
          EventBus.emit(EVENTS_NAME.showInteractiveChallenge, obj2);
        }
      });
    });
  }

  initResources(): void {
    const resources = gameObjectsToObjectPoints(
      this.map.filterObjects("item_spawner", (obj) => obj.name === "Resource"),
    );
    const resourceList = resources.map((resource) => {
      const sprite = this.physics.add
        .sprite(
          resource.x + resource.width / 2,
          resource.y + resource.height / 2,
          "tilesMob",
        )
        .setSize(resource.width, resource.height)
        .setAlpha(0);
      // Read properties BY NAME (order-independent): resource_id, resource_name.
      (resource.properties || []).forEach((p: { name: string; value: unknown }) =>
        sprite.setData(p.name, p.value),
      );
      this.challengeSprites.push(sprite);
      return sprite;
    });
    interface LocalResource {
      data: {
        list: {
          resource_id: string;
          resource_name: string;
        };
      };
    }
    resourceList.forEach((resource) => {
      this.physics.add.overlap(this.player1, resource, (_obj1, obj2) => {
        if (!(obj2 instanceof Tilemaps.Tile)) {
          EventBus.emit(EVENTS_NAME.interactResource, {
            resource_id: parseInt(
              (obj2 as unknown as LocalResource).data.list.resource_id,
            ),
            resource_name: (obj2 as unknown as LocalResource).data.list
              .resource_name,
          });
        }
      });
    });
  }

  initZones(): void {
    const zoneAreas = gameObjectsToObjectPoints(
      this.map.filterObjects("Zones", (obj) => obj.name === "ZoneArea"),
    );

    // Sanctuary rects: any zone whose zone_name starts "SAFE ZONE". Hostile
    // cars refuse to drive into these (Monster.moveToward); THE MERGER
    // ignores them. Reassigned (not appended) on every scene create, so the
    // scene restart after WASTED cannot accumulate duplicates.
    Monster.SAFE_ZONES = zoneAreas
      .filter((z) =>
        String(
          (z.properties || []).find(
            (p: { name: string; value: unknown }) => p.name === "zone_name",
          )?.value ?? "",
        )
          .trim()
          .toLowerCase()
          .startsWith("safe zone"),
      )
      .map((z) => ({ x: z.x, y: z.y, w: z.width, h: z.height }));
    const zoneList = zoneAreas.map((zone) => {
      const sprite = this.physics.add
        .sprite(
          zone.x + zone.width / 2,
          zone.y + zone.height / 2,
          "tilesMob",
        )
        .setSize(zone.width, zone.height)
        .setAlpha(0)
        .setName(
          // Look up zone_name BY NAME (order-independent).
          `${this.mapName} - ${
            (zone.properties || []).find(
              (p: { name: string; value: unknown }) => p.name === "zone_name",
            )?.value ?? ""
          }`,
        );
      this.zoneSprites.push(sprite);
      return sprite;
    });
    zoneList.forEach((zone) => {
      this.physics.add.overlap(this.player1, zone, (_obj1, obj2) => {
        if (!(obj2 instanceof Tilemaps.Tile) && !this.zoneOn) {
          this.zoneOn = true;
          this.zoneId = (obj2 as NamedObject).name;
          EventBus.emit(EVENTS_NAME.zoneEnter, (obj2 as NamedObject).name);
        }
      });
    });
  }

  globalUpdate(): void {
    this.player1.update();

    if (
      !this.physics.world.overlap(this.player1, this.storylineSprites) &&
      this.storylineOn
    ) {
      this.storylineOn = false;
      EventBus.emit(EVENTS_NAME.clearGameUi, "story");
    }
    if (
      !this.physics.world.overlap(this.player1, this.challengeSprites) &&
      this.challengeOn
    ) {
      this.challengeOn = false;
      EventBus.emit(EVENTS_NAME.clearGameUi, "challenge");
    }
    if (
      !this.physics.world.overlap(this.player1, this.zoneSprites) &&
      this.zoneOn
    ) {
      this.zoneOn = false;
      EventBus.emit(EVENTS_NAME.clearGameUi, "zone");
    }

    if (FOV) {
      this.computeFOV();
    }
  }
}
